/**
 * Договор единственного HTTP transport нового клиента (V03, D-053).
 *
 * Endpoint пакета описывает ЧТО запросить (`TransportRequest`), transport
 * решает КАК: cookie сессии, CSRF, корреляция, таймаут и разбор ошибок. Отсюда
 * правило: ни компонент, ни feature не вызывают `fetch`/`axios` сами — иначе
 * каждый второй экран заново «чинит» CSRF и теряет текст ошибки сервера.
 */

export type HttpMethod = 'GET' | 'HEAD' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

/** Безопасные методы: повтор не меняет данных на сервере. */
export const SAFE_METHODS: ReadonlySet<HttpMethod> = new Set<HttpMethod>(['GET', 'HEAD'])

export type QueryValue = string | number | boolean | null | undefined

/**
 * Проверка ответа на границе (D-056): тип TypeScript сам ничего не проверяет,
 * поэтому endpoint передаёт декодер, а transport превращает его отказ в
 * ошибку `invalid-response`, а не в падение экрана на `undefined.name`.
 */
export type ResponseDecoder<T> = (raw: unknown) => T

export interface TransportRequest {
  /** Путь на том же origin: `/api/platform/...`, `/auth/...`. Внешние адреса запрещены. */
  url: string
  method?: HttpMethod
  params?: Readonly<Record<string, QueryValue | readonly QueryValue[]>>
  /**
   * Тело JSON. Строки и `FormData` сюда не кладутся: файл и поля формы — в
   * `formData`, у каждого поля один формат.
   */
  body?: unknown
  /**
   * Тело multipart (загрузка файла). Transport передаёт его fetch как есть: без
   * `JSON.stringify` и без своего `Content-Type` — границу частей (boundary)
   * выставляет браузер. Правила те же, что у JSON: CSRF на изменяющий метод,
   * изменяющий запрос не повторяется, обрыв — `unknown-outcome`. С `body` вместе
   * не задаётся.
   */
  formData?: FormData
  /**
   * Как читать успешный ответ: `json` (по умолчанию), `text`, `ndjson` или `blob`. Ответ
   * `blob` в кэш RTK не кладётся (Redux сериализуем): файл берут командой
   * `requestFile` и отдают на скачивание. Ответ отказа читается как JSON всегда.
   */
  responseType?: ResponseType
  /**
   * Ключ идемпотентности команды с внешним эффектом (контракт v1). Его создаёт
   * use case один раз на намерение человека, transport только передаёт.
   */
  idempotencyKey?: string
  /** Короткое серверное разрешение на статистику подтверждённого кассира; только заголовок, без URL/storage. */
  cashierGrant?: string
  /**
   * Таймаут запроса, мс, включая чтение тела. По умолчанию — у клиента
   * (`defaultTimeoutMs`); загрузке и скачиванию файла задают `FILE_TIMEOUT_MS`.
   */
  timeoutMs?: number
  decode?: ResponseDecoder<unknown>
  /**
   * Не повторять даже безопасное чтение. Для запросов, повтор которых сам по
   * себе вреден: проверка связи 1С (лишняя попытка с неверным паролем
   * приближает блокировку учётной записи, CLAUDE.md §3), `/ready` (повтор прячет
   * то, что проверка и должна показать). Сбой отдаётся сразу, `retryable: true`
   * — повторить может человек.
   */
  noRetry?: boolean
  /**
   * POST без побочных эффектов (поиск по фото): сбой связи - `network`/`timeout`, а не `unknown-outcome`, и 502/503/504 - `unavailable`.
   * Автоповтора нет (`noRetry` задаётся отдельно): повтор - решение человека.
   */
  readOnly?: boolean
  /**
   * Команда с ключом идемпотентности и версией (`idempotencyKey` + `expectedVersion`): сервер узнаёт повтор того же ключа и тела и
   * отдаёт прежний результат, второго эффекта не создаёт (RP-SALE-FAST). Сам transport такой запрос НЕ повторяет: расписание повторов и
   * «попытка N» ведёт вызывающий (очередь команд). Признак меняет одно — отказ без ответа и 5xx помечаются `retryable: true`, то есть
   * «этот же запрос можно отправить ещё раз». Остальные изменяющие запросы (в том числе 1С) остаются неповторяемыми.
   */
  idempotent?: boolean
  /**
   * Служебный фоновый запрос (трассировка продажи): итог НЕ уходит в общую систему связи с сервером — его сбой не показывается
   * как «нет связи» и не освежает возраст данных. Остальные правила (cookie, CSRF, корреляция, неповторяемость команды) прежние.
   */
  background?: boolean
}

/**
 * `ndjson` - поток строк JSON (по одному значению в строке): ответ читается как массив значений. Вместе с
 * {@link RequestObserver} строки отдаются по мере прихода, не дожидаясь конца ответа.
 */
export type ResponseType = 'json' | 'text' | 'blob' | 'ndjson'

/**
 * Наблюдатель за ходом одного запроса. Прогресс передачи и построчное чтение ответа требуют `XMLHttpRequest`
 * (у `fetch` нет ни того, ни другого для тела запроса): если клиент создан без `xhr`, запрос идёт через `fetch`, а наблюдатель получает
 * только строки - после чтения всего ответа. Прогресс при этом не выдумывается: его просто нет.
 */
export interface RequestObserver {
  /** Сколько байт тела уже передано сети и сколько всего (`null` - размер неизвестен). */
  onUploadProgress?: (loaded: number, total: number | null) => void
  /** Очередная строка NDJSON-ответа (только для `responseType: 'ndjson'` и успешного статуса). */
  onLine?: (value: unknown) => void
}

/**
 * Таймаут передачи файла, мс: снимок с телефона на слабой сети или большой
 * xlsx за 30 с по умолчанию не успевают, и загрузка зря кончалась бы
 * «неизвестным исходом».
 */
export const FILE_TIMEOUT_MS = 180_000

/**
 * Заголовки ответа, которые transport отдаёт пакетам (имена — в нижнем
 * регистре). Список закрыт: всё прочее (Set-Cookie, служебное) пакетам не
 * нужно и в мету RTK, то есть в Redux, не попадает.
 */
export const EXPOSED_RESPONSE_HEADERS: readonly string[] = [
  'x-resource-freshness',
  'x-resource-observed-at',
  'x-resource-stale-reason',
  'content-type',
  'content-disposition',
]

export type ResponseHeaders = Readonly<Record<string, string>>

/**
 * Разобранное тело отказа сервера: всё, кроме `code`/`message`/`errors`,
 * которые уже лежат полями ошибки, — `violations[]` правила, `components[]`
 * недоступного поиска, `actualVersion` конфликта. Только JSON-значения, поэтому
 * сериализуемо; разбирать конкретное поле — дело пакета (своим декодером).
 */
export type TransportErrorDetails = Readonly<Record<string, unknown>>

/**
 * Вид ошибки. Экран показывает их по-разному (F06: loading, empty, forbidden,
 * unavailable и error — разные состояния), поэтому вид — перечисление, а не
 * число статуса, которое каждый экран толковал бы сам.
 */
export type TransportErrorKind =
  /** 401: сессии нет или она истекла — общий механизм входа (D-085). */
  | 'unauthorized'
  /** 403: доступа к ресурсу/действию нет. Повтор не поможет. */
  | 'forbidden'
  /** 400 ANTIFORGERY_FAILED и после обновления токена. */
  | 'csrf'
  | 'not-found'
  | 'conflict'
  | 'validation'
  /**
   * 502/503/504 и `access-unavailable`: служба или проверка прав недоступны.
   * На изменяющий запрос — только когда сервер НАЗВАЛ причину кодом в теле
   * (`access-unavailable`, `onec-unavailable`); 502/503/504 без кода на
   * изменяющий запрос — `unknown-outcome`.
   */
  | 'unavailable'
  | 'http'
  /** Безопасный запрос не дошёл: сети нет. Повторить можно. */
  | 'network'
  /** Безопасный запрос не дождался ответа. Повторить можно. */
  | 'timeout'
  /**
   * Изменяющий запрос ушёл, но ответа нет (сеть, таймаут, 502/503/504 прокси
   * без кода причины — например, Gateway не дождался службы). Выполнен ли он —
   * неизвестно; повтор запрещён, нужна проверка состояния (ADR-11, D-085).
   */
  | 'unknown-outcome'
  /** Ответ пришёл, но не прошёл декодер или не JSON. */
  | 'invalid-response'

/**
 * Ошибка transport. Простой сериализуемый объект, а не экземпляр Error: RTK
 * Query кладёт её в Redux, и состояние обязано оставаться сериализуемым.
 */
export interface TransportError {
  kind: TransportErrorKind
  /** HTTP-статус, если ответ был. */
  status?: number
  /** Код ошибки сервера (`resource-not-found`, `ANTIFORGERY_FAILED`) или вида. */
  code: string
  /**
   * Текст для человека. Сообщение сервера передаётся ДОСЛОВНО (ADR-8): текст
   * 1С и причина отказа — то, ради чего экран показывает ошибку.
   */
  message: string
  /** Корреляция запроса: в отчёт об ошибке и в журнал службы. */
  correlationId: string
  /**
   * Можно ли повторить ТОТ ЖЕ запрос без риска двойного эффекта: безопасное чтение после сбоя связи и команда `idempotent`
   * (тот же ключ и тело) после отказа без ответа или 5xx.
   */
  retryable: boolean
  /** Ошибки полей формы, если сервер их прислал. */
  fields?: Readonly<Record<string, readonly string[]>>
  /** Остальное тело отказа, если сервер прислал объект (см. `TransportErrorDetails`). */
  details?: TransportErrorDetails
}

export type TransportResult<T = unknown> =
  | { ok: true; data: T; status: number; correlationId: string; headers?: ResponseHeaders }
  | { ok: false; error: TransportError }

export function isTransportError(value: unknown): value is TransportError {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Partial<TransportError>
  return (
    typeof candidate.kind === 'string' && typeof candidate.code === 'string' && typeof candidate.message === 'string'
  )
}
